import { observer } from 'mobx-react-lite'
import { Box, Button, TextareaWithLabel, TextFieldWithLabel, Typography } from 'shared/ui'
import { useTextareaAutoResize } from 'shared/hooks'
import { MessageField } from 'widgets/MessageField'
import { type ActionsStore } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/Actions/store/ActionsStore'
import styles from './styles.module.scss'

export type IAfterCallModalContentProps = {
  store: ActionsStore
  modalId: string
  editingId: string | null
}

export const DuringCallModalContent = observer(
  ({ store, modalId, editingId }: IAfterCallModalContentProps) => {
    const {
      messageFieldStore,
      onCloseModal,
      onSave,
      onDelete,
      name,
      triggerDescription,
      voiceResponse,
      setName,
      setTriggerDescription,
      setVoiceResponse,
    } = store
    const scenarioTextareaRef = useTextareaAutoResize(triggerDescription, 84, 184)
    const agentVoiceResponseTextareaRef = useTextareaAutoResize(voiceResponse, 84, 184)
    const isMessageEmpty =
      !messageFieldStore.text.trim() && messageFieldStore.attachments.length === 0
    const isDisabled =
      !name || !triggerDescription || isMessageEmpty || messageFieldStore.loadingAttachment

    return (
      <Box display={'flex'} flexDirection={'column'}>
        <div className={styles.duringCallContent}>
          <TextFieldWithLabel
            labelProps={{ label: { text: 'Title' } }}
            textFieldProps={{
              value: name,
              onChange: setName,
              InputProps: { placeholder: 'e.g. Pricing request' },
            }}
            limit={255}
          />
          <TextareaWithLabel
            ref={scenarioTextareaRef}
            value={triggerDescription}
            rows={3}
            name={'scenarioDescription'}
            onChange={setTriggerDescription}
            placeholder={
              'e.g. The caller asks about pricing, plans, or how much your service costs.'
            }
            labelProps={{
              marginBottom: 0,
              questionTooltipProps: {
                label: 'Describe when this SMS should be sent',
                placement: 'top',
              },
              label: {
                text: 'Scenario description',
              },
            }}
          />
          <TextareaWithLabel
            ref={agentVoiceResponseTextareaRef}
            value={voiceResponse}
            rows={3}
            name={'agentVoiceResponse'}
            onChange={setVoiceResponse}
            placeholder={
              "e.g. I'll send you a quick text message with a link to our pricing page so you can review the plans."
            }
            labelProps={{
              marginBottom: 0,
              questionTooltipProps: {
                label: 'What the agent says before sending the SMS',
                placement: 'top',
              },
              label: {
                text: 'Agent voice response',
                secondaryText: '(optional)',
              },
            }}
          />
          <div>
            <Typography
              ariaLabel={'SMS message'}
              variant={'text-input-label-small'}
              color={'var(--content-primary-secondary)'}
              marginTop={2}
              marginBottom={6}
              tag='div'
            >
              SMS message
            </Typography>
            <div className={styles.alertContainer} ref={messageFieldStore.setAlertElement} />
            <MessageField messageFieldStore={messageFieldStore} />
          </div>
        </div>
        <div className={styles.footer}>
          {editingId && (
            <Button
              typeBtn={'contained'}
              contained={'secondary'}
              intent={'destructive'}
              text={'Delete action'}
              onClick={() => onDelete(modalId, editingId)}
            />
          )}
          <div className={styles.footerRight}>
            <Button
              typeBtn={'contained'}
              text={'Cancel'}
              contained={'secondary'}
              onClick={() => onCloseModal(modalId)}
            />
            <Button
              typeBtn={'contained'}
              text={'Save'}
              disabled={isDisabled}
              onClick={() => onSave(modalId, editingId)}
            />
          </div>
        </div>
      </Box>
    )
  }
)
